import type { BBox, DetectionItem } from '@EcamV2/api';
import type { VehicleDetection } from '../components/VehicleImageWithBbox';

export type { DetectionItem };

/** Fallback vehicle/plate box saved for a photo selected as evidence that has no detections at all (never a generic fallback). */
export const DEFAULT_EVIDENCE_BBOX: BBox = { x1: 480, y1: 300, x2: 1440, y2: 900 };

/** Intersection-over-union of two axis-aligned boxes, in [0, 1]. Mirrors the BE's `calculateIoU`. */
export function calculateIoU(boxA: BBox, boxB: BBox): number {
    const x1 = Math.max(boxA.x1, boxB.x1);
    const y1 = Math.max(boxA.y1, boxB.y1);
    const x2 = Math.min(boxA.x2, boxB.x2);
    const y2 = Math.min(boxA.y2, boxB.y2);

    if (x2 <= x1 || y2 <= y1) return 0;

    const intersection = (x2 - x1) * (y2 - y1);
    const areaA = (boxA.x2 - boxA.x1) * (boxA.y2 - boxA.y1);
    const areaB = (boxB.x2 - boxB.x1) * (boxB.y2 - boxB.y1);
    const union = areaA + areaB - intersection;

    return union > 0 ? intersection / union : 0;
}

/** True when the box has positive width and height — the shape rendering code treats as drawable. */
export function isValidBBox(bbox?: BBox | null): bbox is BBox {
    return !!bbox && bbox.x2 > bbox.x1 && bbox.y2 > bbox.y1;
}

/** Axis-aligned bounding box (min/max corners) of an 8-value 4-point polygon [x1,y1,x2,y2,x3,y3,x4,y4]. */
export function warpedBoxToBBox(warpedBox: number[]): BBox | null {
    if (!warpedBox || warpedBox.length < 8) return null;
    return {
        x1: Math.min(warpedBox[0], warpedBox[2], warpedBox[4], warpedBox[6]),
        y1: Math.min(warpedBox[1], warpedBox[3], warpedBox[5], warpedBox[7]),
        x2: Math.max(warpedBox[0], warpedBox[2], warpedBox[4], warpedBox[6]),
        y2: Math.max(warpedBox[1], warpedBox[3], warpedBox[5], warpedBox[7]),
    };
}

/**
 * Uppercases and strips whitespace/dashes for plate comparison, mirroring EvidencePhotoGroup's
 * normalize(). A placeholder OCR read — the AI pipeline's "no read" marker, seen as "NaN", "NAN"
 * and "nan" depending on the source — normalizes to `''`, so it can never match a real plate and a
 * placeholder on one candidate can't falsely match a placeholder on another (a case-sensitive-only
 * check would miss "NAN"/"nan").
 */
export const normalizePlateText = (s?: string | null): string => {
    const normalized = (s ?? '').toUpperCase().replace(/[\s-]/g, '');
    return normalized === 'NAN' ? '' : normalized;
};

/** Same default acceptance threshold as the BE's `findMatchingDetection`/`findBestDetection`. */
export const DEFAULT_IOU_THRESHOLD = 0.7;

/**
 * Picks whichever of the raw `detections` best matches `vehicleBbox` by IoU, only accepted above
 * `iouThreshold`. Mirrors the BE's `findBestDetection` structure exactly: candidate-building
 * (a detection with no vehicle box of its own falls back to its plate box, one with neither is
 * dropped entirely, and a placeholder plate text is treated as no text) happens inline here rather
 * than through a separate exported converter.
 */
export function findBestDetection(
    detections: DetectionItem[],
    vehicleBbox: BBox,
    iouThreshold: number = DEFAULT_IOU_THRESHOLD
): VehicleDetection | null {
    const candidates: VehicleDetection[] = detections.flatMap((det) => {
        const plateBbox = warpedBoxToBBox(det.warpedBox || []);
        const candidateVehicleBbox = warpedBoxToBBox(det.car?.warpedBox || []) ?? plateBbox;
        if (!candidateVehicleBbox) return [];
        const plateText = det.text && normalizePlateText(det.text) ? det.text : null;
        return [{ vehicleBbox: candidateVehicleBbox, plateBbox, plateText, vehicleConfidence: det.car?.confidence }];
    });

    let bestIou = 0;
    let best: VehicleDetection | null = null;
    for (const c of candidates) {
        const iou = calculateIoU(vehicleBbox, c.vehicleBbox as BBox);
        if (iou > bestIou) {
            bestIou = iou;
            best = c;
        }
    }
    return bestIou > iouThreshold ? best : null;
}
